'use client';

import { createClient } from '@/lib/supabase/client';
import { createHubNotification } from '@/lib/db/hub-notifications';
import { sendApprovalEmail, sendRejectionEmail, sendRankChangeEmail, sendCorroborationEmail, sendSubmissionConfirmationEmail } from '@/lib/email/transactional';

export interface ContributionInsert {
  title: string;
  body: string;
  eraId: string;
  topicId?: string;
  customTopicLabel?: string;
  region?: string;
  certaintyLabel: 'oral_tradition' | 'sourced_claim' | 'firsthand_account';
  isAnonymous: boolean;
  wordCount: number;
  excerpt?: string;
  featuredImageUrl?: string;
  audioUrl?: string;
  audioDurationSeconds?: number;
  audioFileSizeBytes?: number;
  audioUploadedAt?: string;
}

export interface CitationInsert {
  title: string;
  author?: string;
  publicationDate?: string;
  url?: string;
  archiveSource?: string;
  documentType?: string;
  verificationStatus?: string;
}

export interface ContributionRow {
  id: string;
  title: string;
  body: string;
  era_id: string | null;
  topic_id: string | null;
  region: string | null;
  certainty_label: string;
  status: string;
  author_id: string | null;
  is_anonymous: boolean;
  word_count: number;
  excerpt: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  // PostgREST schema cache errors are transient — do NOT treat as fatal schema errors
  if (error.message && /could not find the table|schema cache/i.test(error.message)) {
    return false;
  }
  if (error.code && typeof error.code === 'string') {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    return /relation.*does not exist|column.*does not exist|function.*does not exist|syntax error/i.test(error.message);
  }
  return false;
}

// Rate limit: max 5 submissions per 24 hours per user
const RATE_LIMIT_MAX = 5;
const RATE_LIMIT_WINDOW_MS = 24 * 60 * 60 * 1000;

export async function checkSubmissionRateLimit(userId: string | null): Promise<{ allowed: boolean; remaining: number; resetAt: Date }> {
  if (!userId) {
    // Anonymous users: use localStorage-based rate limiting
    if (typeof window === 'undefined') return { allowed: true, remaining: RATE_LIMIT_MAX, resetAt: new Date() };
    const key = 'griott_anon_submissions';
    const stored = localStorage.getItem(key);
    const now = Date.now();
    let submissions: number[] = stored ? JSON.parse(stored) : [];
    // Filter to last 24h
    submissions = submissions.filter((ts) => now - ts < RATE_LIMIT_WINDOW_MS);
    const remaining = Math.max(0, RATE_LIMIT_MAX - submissions.length);
    const resetAt = submissions.length > 0 ? new Date(submissions[0] + RATE_LIMIT_WINDOW_MS) : new Date(now + RATE_LIMIT_WINDOW_MS);
    return { allowed: submissions.length < RATE_LIMIT_MAX, remaining, resetAt };
  }

  const supabase = createClient();
  const since = new Date(Date.now() - RATE_LIMIT_WINDOW_MS).toISOString();

  // ── Fix 10: Fetch oldest submission within window to anchor reset time ──
  const { data: submissions, error } = await supabase
    .from('contributions')
    .select('id, created_at')
    .eq('author_id', userId)
    .gte('created_at', since)
    .order('created_at', { ascending: true });

  if (error) return { allowed: true, remaining: RATE_LIMIT_MAX, resetAt: new Date() };

  const used = submissions?.length || 0;
  const remaining = Math.max(0, RATE_LIMIT_MAX - used);

  // Reset time is anchored to the oldest submission in the window + 24h
  const oldestSubmission = submissions && submissions.length > 0 ? submissions[0] : null;
  const resetAt = oldestSubmission
    ? new Date(new Date(oldestSubmission.created_at).getTime() + RATE_LIMIT_WINDOW_MS)
    : new Date(Date.now() + RATE_LIMIT_WINDOW_MS);

  return {
    allowed: used < RATE_LIMIT_MAX,
    remaining,
    resetAt,
  };
}

export async function submitContribution(
  data: ContributionInsert,
  citations: CitationInsert[]
): Promise<{ id: string } | null> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();

  // Rate limit check
  const rateCheck = await checkSubmissionRateLimit(data.isAnonymous ? null : (user?.id || null));
  if (!rateCheck.allowed) {
    throw new Error(`Rate limit reached. You can submit up to ${RATE_LIMIT_MAX} contributions per 24 hours. Try again after ${rateCheck.resetAt.toLocaleTimeString()}.`);
  }

  try {
    // If a custom topic label is provided, create the topic first
    let resolvedTopicId: string | null = data.topicId || null;
    if (data.customTopicLabel && data.customTopicLabel.trim()) {
      const slug = data.customTopicLabel.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const newTopicId = `topic-custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const { data: newTopic, error: topicError } = await supabase
        .from('topics')
        .insert({
          id: newTopicId,
          era_id: data.eraId,
          label: data.customTopicLabel.trim(),
          slug: slug || newTopicId,
        })
        .select('id')
        .single();
      if (!topicError && newTopic) {
        resolvedTopicId = newTopic.id;
      }
    }

    const { data: contrib, error: contribError } = await supabase
      .from('contributions')
      .insert({
        title: data.title,
        body: data.body,
        era_id: data.eraId,
        topic_id: resolvedTopicId,
        region: data.region || null,
        certainty_label: data.certaintyLabel,
        is_anonymous: data.isAnonymous,
        word_count: data.wordCount,
        excerpt: data.excerpt || data.body.substring(0, 200),
        author_id: data.isAnonymous ? null : (user?.id || null),
        featured_image_url: data.featuredImageUrl || null,
        audio_url: data.audioUrl || null,
        audio_duration_seconds: data.audioDurationSeconds ?? null,
        audio_file_size_bytes: data.audioFileSizeBytes ?? null,
        audio_uploaded_at: data.audioUploadedAt || null,
      })
      .select('id')
      .single();

    if (contribError) {
      if (isSchemaError(contribError)) throw contribError;
      console.error('Contribution insert error:', contribError.message);
      return null;
    }

    // Track anonymous submissions in localStorage
    if (data.isAnonymous && typeof window !== 'undefined') {
      const key = 'griott_anon_submissions';
      const stored = localStorage.getItem(key);
      const now = Date.now();
      let submissions: number[] = stored ? JSON.parse(stored) : [];
      submissions = submissions.filter((ts) => now - ts < RATE_LIMIT_WINDOW_MS);
      submissions.push(now);
      localStorage.setItem(key, JSON.stringify(submissions));
    }

    if (citations.length > 0 && contrib) {
      const citationRows = citations.map((c) => ({
        contribution_id: contrib.id,
        title: c.title,
        author: c.author || null,
        publication_date: c.publicationDate || null,
        url: c.url || null,
        archive_source: c.archiveSource || null,
        document_type: c.documentType || 'Other',
        verification_status: c.verificationStatus || 'unverified',
      }));

      const { error: citError } = await supabase.from('citations').insert(citationRows);
      if (citError && isSchemaError(citError)) throw citError;
    }

    // Send submission confirmation email to non-anonymous contributors
    if (contrib && !data.isAnonymous && user?.id) {
      await sendSubmissionConfirmationEmail({
        recipientUserId: user.id,
        contributionTitle: data.title,
        contributionId: contrib.id,
      });
    }

    return contrib ? { id: contrib.id } : null;
  } catch (error: any) {
    console.error('submitContribution error:', error.message);
    throw error;
  }
}

export async function getContributionsForQueue(): Promise<any[]> {
  const supabase = createClient();

  try {
    const { data, error } = await supabase
      .from('contributions')
      .select(`
        id, title, era_id, topic_id, region, certainty_label, status,
        author_id, is_anonymous, word_count, excerpt, created_at,
        citations(id),
        moderation_flags(flag_type)
      `)
      .order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('getContributionsForQueue error:', error.message);
      return [];
    }

    const rows = data || [];

    // Fetch author profiles separately to avoid schema cache errors
    const authorIds = [...new Set(rows.filter((r: any) => !r.is_anonymous && r.author_id).map((r: any) => r.author_id))];
    let profileMap: Record<string, { full_name: string; username: string; reputation_score: number; tier: string }> = {};
    if (authorIds.length > 0) {
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, full_name, username, reputation_score, tier')
        .in('id', authorIds);
      for (const p of profiles || []) {
        profileMap[p.id] = p;
      }
    }

    return rows.map((row: any) => {
      const submittedDate = new Date(row.created_at);
      const now = new Date();
      const daysSince = Math.floor((now.getTime() - submittedDate.getTime()) / (1000 * 60 * 60 * 24));
      const slaStatus = daysSince <= 3 ? 'on-track' : daysSince <= 7 ? 'due-soon' : 'overdue';
      const profile = row.is_anonymous ? null : profileMap[row.author_id];

      return {
        id: row.id,
        title: row.title,
        era: row.era_id,
        topic: row.topic_id,
        certaintyLabel: row.certainty_label,
        author: row.is_anonymous ? 'Anonymous' : (profile?.full_name || 'Unknown'),
        authorReputation: profile?.reputation_score || 0,
        authorTier: profile?.tier || 'new',
        submittedAt: submittedDate.toLocaleString('en-GB', {
          day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        }),
        daysSinceSubmission: daysSince,
        slaStatus,
        wordCount: row.word_count,
        citationCount: row.citations?.length || 0,
        flags: (row.moderation_flags || []).map((f: any) => f.flag_type),
        isAnonymous: row.is_anonymous,
        status: row.status,
        excerpt: row.excerpt || '',
        region: row.region || '',
      };
    });
  } catch (error: any) {
    console.error('getContributionsForQueue error:', error.message);
    throw error;
  }
}

export async function updateContributionStatus(
  id: string,
  status: 'published' | 'rejected',
  rejectionReason?: string
): Promise<boolean> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();

  try {
    const { error } = await supabase
      .from('contributions')
      .update({
        status,
        reviewed_by: user?.id || null,
        reviewed_at: new Date().toISOString(),
        rejection_reason: rejectionReason || null,
      })
      .eq('id', id);

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('updateContributionStatus error:', error.message);
      return false;
    }

    // Fetch contribution details for notification
    const { data: contrib } = await supabase
      .from('contributions')
      .select('author_id, title')
      .eq('id', id)
      .single();

    if (contrib?.author_id) {
      // Capture tier before recalculation
      const { data: profileBefore } = await supabase
        .from('user_profiles')
        .select('tier, reputation_score')
        .eq('id', contrib.author_id)
        .maybeSingle();

      // Recalculate reputation
      await supabase.rpc('recalculate_reputation', { p_user_id: contrib.author_id });

      // Capture tier after recalculation
      const { data: profileAfter } = await supabase
        .from('user_profiles')
        .select('tier, reputation_score')
        .eq('id', contrib.author_id)
        .maybeSingle();

      // Send hub notification to contributor
      if (status === 'published') {
        await createHubNotification({
          recipientId: contrib.author_id,
          type: 'contribution_approved',
          title: 'Your contribution was approved!',
          body: `"${contrib.title}" has been reviewed and published to the archive.`,
          link: `/contributions/${id}`,
          metadata: { contribution_id: id, contribution_title: contrib.title },
        });

        // Send transactional email (respects user preferences)
        await sendApprovalEmail({
          recipientUserId: contrib.author_id,
          contributionTitle: contrib.title,
          contributionId: id,
        });
      } else if (status === 'rejected') {
        await createHubNotification({
          recipientId: contrib.author_id,
          type: 'contribution_rejected',
          title: 'Your contribution was not approved',
          body: rejectionReason
            ? `"${contrib.title}" was rejected: ${rejectionReason}`
            : `"${contrib.title}" did not meet the current curation standards.`,
          link: `/contributions/${id}`,
          metadata: { contribution_id: id, contribution_title: contrib.title, rejection_reason: rejectionReason || null },
        });

        // Send transactional email (respects user preferences)
        await sendRejectionEmail({
          recipientUserId: contrib.author_id,
          contributionTitle: contrib.title,
          contributionId: id,
          reason: rejectionReason,
        });
      }

      // Detect rank change and notify
      if (profileBefore && profileAfter && profileBefore.tier !== profileAfter.tier) {
        await createHubNotification({
          recipientId: contrib.author_id,
          type: 'rank_changed',
          title: 'Your contributor rank changed!',
          body: `You moved from ${profileBefore.tier} to ${profileAfter.tier}.`,
          link: '/contributor-leaderboard',
          metadata: { old_rank: profileBefore.tier, new_rank: profileAfter.tier, reputation_score: profileAfter.reputation_score },
        });

        await sendRankChangeEmail({
          recipientUserId: contrib.author_id,
          oldRank: profileBefore.tier,
          newRank: profileAfter.tier,
          reputationScore: profileAfter.reputation_score ?? 0,
        });
      }
    }

    return true;
  } catch (error: any) {
    console.error('updateContributionStatus error:', error.message);
    throw error;
  }
}

export async function getContributionsByUser(userId: string): Promise<any[]> {
  const supabase = createClient();

  try {
    const { data, error } = await supabase
      .from('contributions')
      .select(`
        id, title, era_id, topic_id, certainty_label, status,
        word_count, created_at, updated_at,
        corroborations(id)
      `)
      .eq('author_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('getContributionsByUser error:', error.message);
      return [];
    }

    return (data || []).map((row: any) => ({
      id: row.id,
      title: row.title,
      era: row.era_id,
      topic: row.topic_id,
      status: row.status,
      certaintyLabel: row.certainty_label,
      submittedAt: new Date(row.created_at).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
      }),
      publishedAt: row.status === 'published' ? new Date(row.updated_at).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
      }) : undefined,
      corroborations: row.corroborations?.length || 0,
      wordCount: row.word_count,
    }));
  } catch (error: any) {
    console.error('getContributionsByUser error:', error.message);
    throw error;
  }
}

export async function getPublishedContributions(limit = 20): Promise<any[]> {
  const supabase = createClient();

  try {
    const { data, error } = await supabase
      .from('contributions')
      .select(`
        id, title, era_id, topic_id, region, certainty_label, excerpt, created_at,
        author_id, is_anonymous,
        corroborations(id)
      `)
      .eq('status', 'published')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('getPublishedContributions error:', error.message);
      return [];
    }

    const rows = data || [];

    // Fetch author names separately to avoid schema cache errors
    const authorIds = [...new Set(rows.filter((r: any) => !r.is_anonymous && r.author_id).map((r: any) => r.author_id))];
    let nameMap: Record<string, string> = {};
    if (authorIds.length > 0) {
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, full_name, username')
        .in('id', authorIds);
      for (const p of profiles || []) {
        nameMap[p.id] = p.full_name || p.username || '';
      }
    }

    return rows.map((row: any) => ({
      ...row,
      user_profiles: row.is_anonymous ? null : { full_name: nameMap[row.author_id] || '', username: nameMap[row.author_id] || '' },
    }));
  } catch (error: any) {
    console.error('getPublishedContributions error:', error.message);
    throw error;
  }
}

/**
 * Add a corroboration (confirm or dispute) on a published contribution.
 * Fires a hub notification + transactional email to the contribution author.
 */
export async function addCorroboration(params: {
  contributionId: string;
  type: 'confirm' | 'dispute';
  note?: string;
}): Promise<{ success: boolean; error?: string }> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { success: false, error: 'Not authenticated' };

  try {
    const { error: insertError } = await supabase
      .from('corroborations')
      .insert({
        contribution_id: params.contributionId,
        user_id: user.id,
        corroboration_type: params.type,
        note: params.note ?? null,
      });

    if (insertError) {
      if (insertError.code === '23505') return { success: false, error: 'Already corroborated' };
      console.error('addCorroboration insert error:', insertError.message);
      return { success: false, error: insertError.message };
    }

    // Only notify for 'confirm' corroborations
    if (params.type === 'confirm') {
      // Fetch contribution + author info
      const { data: contrib } = await supabase
        .from('contributions')
        .select('title, author_id')
        .eq('id', params.contributionId)
        .maybeSingle();

      if (contrib?.author_id && contrib.author_id !== user.id) {
        // Fetch corroborator display name
        const { data: corroboratorProfile } = await supabase
          .from('user_profiles')
          .select('full_name, username')
          .eq('id', user.id)
          .maybeSingle();
        const corroboratorName = corroboratorProfile?.full_name || corroboratorProfile?.username || 'A community member';

        // Hub notification
        await createHubNotification({
          recipientId: contrib.author_id,
          type: 'corroboration_received',
          title: 'New corroboration received',
          body: `${corroboratorName} corroborated your contribution "${contrib.title}".`,
          link: `/contributions/${params.contributionId}`,
          metadata: { contribution_id: params.contributionId, contribution_title: contrib.title },
        });

        // Transactional email (respects user preferences)
        await sendCorroborationEmail({
          recipientUserId: contrib.author_id,
          contributionTitle: contrib.title,
          contributionId: params.contributionId,
          corroboratorName,
        });

        // Recalculate reputation and check for rank change
        const { data: profileBefore } = await supabase
          .from('user_profiles')
          .select('tier, reputation_score')
          .eq('id', contrib.author_id)
          .maybeSingle();

        await supabase.rpc('recalculate_reputation', { p_user_id: contrib.author_id });

        const { data: profileAfter } = await supabase
          .from('user_profiles')
          .select('tier, reputation_score')
          .eq('id', contrib.author_id)
          .maybeSingle();

        if (profileBefore && profileAfter && profileBefore.tier !== profileAfter.tier) {
          await createHubNotification({
            recipientId: contrib.author_id,
            type: 'rank_changed',
            title: 'Your contributor rank changed!',
            body: `You moved from ${profileBefore.tier} to ${profileAfter.tier}.`,
            link: '/contributor-leaderboard',
            metadata: { old_rank: profileBefore.tier, new_rank: profileAfter.tier, reputation_score: profileAfter.reputation_score },
          });

          await sendRankChangeEmail({
            recipientUserId: contrib.author_id,
            oldRank: profileBefore.tier,
            newRank: profileAfter.tier,
            reputationScore: profileAfter.reputation_score ?? 0,
          });
        }
      }
    }

    return { success: true };
  } catch (err: any) {
    console.error('addCorroboration error:', err.message);
    return { success: false, error: err.message };
  }
}

export async function updateContributionMetadata(
  id: string,
  updates: {
    eraId?: string;
    topicId?: string;
    region?: string;
    title?: string;
    body?: string;
    certaintyLabel?: string;
  }
): Promise<boolean> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  try {
    // Fetch current values for audit trail diff
    const { data: current } = await supabase
      .from('contributions')
      .select('era_id, topic_id, region, title, body, certainty_label, eras(name)')
      .eq('id', id)
      .single();

    const payload: Record<string, any> = { updated_at: new Date().toISOString() };
    if (updates.eraId !== undefined) payload.era_id = updates.eraId;
    if (updates.topicId !== undefined) payload.topic_id = updates.topicId;
    if (updates.region !== undefined) payload.region = updates.region;
    if (updates.title !== undefined) payload.title = updates.title;
    if (updates.certaintyLabel !== undefined) payload.certainty_label = updates.certaintyLabel;
    if (updates.body !== undefined) {
      payload.body = updates.body;
      // Recalculate word count from plain text
      const plainText = updates.body.replace(/<[^>]+>/g, ' ');
      const words = plainText.trim().split(/\s+/).filter(Boolean);
      payload.word_count = words.length;
      // Update excerpt
      payload.excerpt = plainText.substring(0, 250);
    }

    const { error } = await supabase
      .from('contributions')
      .update(payload)
      .eq('id', id);

    if (error) {
      console.error('updateContributionMetadata error:', error.message);
      return false;
    }

    // ── Write audit trail entries ──────────────────────────────────────────
    const auditEntries: {
      contribution_id: string;
      editor_id: string;
      field_name: string;
      old_value: string | null;
      new_value: string | null;
    }[] = [];

    if (updates.eraId !== undefined && current) {
      const oldEraName = (current as any).eras?.name || current.era_id || null;
      // Fetch new era name
      let newEraName: string | null = updates.eraId || null;
      if (updates.eraId) {
        const { data: eraRow } = await supabase.from('eras').select('name').eq('id', updates.eraId).single();
        if (eraRow) newEraName = eraRow.name;
      }
      if (oldEraName !== newEraName) {
        auditEntries.push({ contribution_id: id, editor_id: user.id, field_name: 'era', old_value: oldEraName, new_value: newEraName });
      }
    }

    if (updates.certaintyLabel !== undefined && current) {
      const oldVal = current.certainty_label || null;
      const newVal = updates.certaintyLabel || null;
      if (oldVal !== newVal) {
        auditEntries.push({ contribution_id: id, editor_id: user.id, field_name: 'certainty_label', old_value: oldVal, new_value: newVal });
      }
    }

    if (updates.region !== undefined && current) {
      const oldVal = current.region || null;
      const newVal = updates.region || null;
      if (oldVal !== newVal) {
        auditEntries.push({ contribution_id: id, editor_id: user.id, field_name: 'region', old_value: oldVal, new_value: newVal });
      }
    }

    if (updates.title !== undefined && current) {
      const oldVal = current.title || null;
      const newVal = updates.title || null;
      if (oldVal !== newVal) {
        auditEntries.push({ contribution_id: id, editor_id: user.id, field_name: 'title', old_value: oldVal, new_value: newVal });
      }
    }

    if (updates.body !== undefined && current) {
      const oldPlain = (current.body || '').replace(/<[^>]+>/g, ' ').trim().substring(0, 120);
      const newPlain = (updates.body || '').replace(/<[^>]+>/g, ' ').trim().substring(0, 120);
      if (oldPlain !== newPlain) {
        auditEntries.push({ contribution_id: id, editor_id: user.id, field_name: 'body', old_value: oldPlain || null, new_value: newPlain || null });
      }
    }

    if (auditEntries.length > 0) {
      await supabase.from('story_edit_audit_trail').insert(auditEntries);
    }

    return true;
  } catch (error: any) {
    console.error('updateContributionMetadata error:', error.message);
    return false;
  }
}

export async function linkStoryToProject(
  contributionId: string,
  projectId: string
): Promise<boolean> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  try {
    const { error } = await supabase
      .from('doc_project_stories')
      .insert({
        project_id: projectId,
        contribution_id: contributionId,
        linked_by: user.id,
      });

    if (error) {
      if (error.code === '23505') return true; // already linked
      console.error('linkStoryToProject error:', error.message);
      return false;
    }
    return true;
  } catch (error: any) {
    console.error('linkStoryToProject error:', error.message);
    return false;
  }
}

export async function getUserDocProjects(): Promise<{ id: string; title: string; category: string }[]> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  try {
    const { data, error } = await supabase
      .from('doc_projects')
      .select('id, title, category')
      .eq('creator_id', user.id)
      .eq('status', 'active')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('getUserDocProjects error:', error.message);
      return [];
    }
    return (data || []).map((r: any) => ({ id: r.id, title: r.title, category: r.category }));
  } catch (error: any) {
    console.error('getUserDocProjects error:', error.message);
    return [];
  }
}
